/**
 * This is a template to render feedback on specific actions the Dasher
 * either did or did not perform in the context of a delivery issue.
 */

import * as React from 'react'

import ComponentListCell from '@doordash/component-list-cell'
import { Text, Colors, Icon, Stack } from '@doordash/design-language'

import { DeliveryIssueStepResults } from '../../../../models/types'
import { StepPositions } from './DeliveryIssueStep'
import {
  DeliveryIssuesIconContainer,
  StepIcon,
  TimelineVerticalLineFirst,
  TimelineVerticalLineLast,
  TimelineVerticalLineMiddle,
  TimelineWrapper,
} from './styles'

type TimelineVerticalLineProps = {
  position: StepPositions
}

const TimelineVerticalLine: React.FC<TimelineVerticalLineProps> = ({
  position,
}) => {
  if (position === StepPositions.First) {
    return <TimelineVerticalLineFirst />
  }
  if (position === StepPositions.Last) {
    return <TimelineVerticalLineLast />
  }
  return <TimelineVerticalLineMiddle />
}

type DeliveryIssueIconProps = {
  result: DeliveryIssueStepResults
}

export const DeliveryIssueIcon: React.FC<DeliveryIssueIconProps> = ({
  result,
}) => (
  <DeliveryIssuesIconContainer>
    <StepIcon
      size={Icon.Sizes.Medium}
      type={
        result === DeliveryIssueStepResults.Success
          ? Icon.Types.CheckCircleFill
          : Icon.Types.WarningCircleFill
      }
      color={
        result === DeliveryIssueStepResults.Success
          ? Icon.Colors.SystemGreen50
          : Icon.Colors.SystemRed80
      }
    />
  </DeliveryIssuesIconContainer>
)

type DeliveryIssueStepTemplateProps = {
  title: string
  subtext: string
  position: StepPositions
}

const DeliveryIssueStepTemplate: React.FC<DeliveryIssueStepTemplateProps &
  DeliveryIssueIconProps> = ({
  result,
  subtext,
  title,
  position,
  ...props
}) => (
  <ComponentListCell
    title={
      <Stack size={Stack.Sizes.XxSmall}>
        <Text styles={Text.Styles.Bold16}>{title}</Text>
      </Stack>
    }
    subtext={
      <Text
        styles={Text.Styles.PageDescriptionBody}
        color={Colors.SystemGrey60}
      >
        {subtext}
      </Text>
    }
    size={ComponentListCell.Sizes.Small}
    shouldInsetStartAtTitle
    renderBeforeContent={() => (
      <TimelineWrapper>
        <TimelineVerticalLine position={position} />
        <DeliveryIssueIcon result={result} />
      </TimelineWrapper>
    )}
    isSeparatorVisible={position !== StepPositions.Last}
    {...props}
  />
)

export default DeliveryIssueStepTemplate
